import { useEffect, useMemo, useState } from "react";
import Swal from 'sweetalert2';
import {
    Backdrop,
    Box,
    Typography,
    Stack
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';

import { formatCurrency } from "../utils";
import { chipPaymentStatus } from "../../utils/chipPaymentStatus";
import { chipOrderStatus } from '../../utils/chipOrderStatus';

import SearchNotFound from "../../utils/SearchNotFound";
import CustomSearch from "../custom_components/CustomSearch";
import CustomButton from "../custom_components/CustomButton";
import CustomModalHeader from "../custom_components/CustomModalHeader";
import CustomSelect from "../custom_components/CustomSelect";

const AddOrder = ({ open, onClose, listID, listOrders, onUpdate }) => {

    const apiUrl = process.env.REACT_APP_API_URL;

    const [ROWS, setROWS] = useState([]);

    const [loadingOrders, setLoadingOrders] = useState(false);
    const [loadingAddOrder, setLoadingAddOrder] = useState(false);

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [status, setStatus] = useState("");

    const [selectedIds, setSelectedIds] = useState([]);
    const [rowSelectionModel, setRowSelectionModel] = useState({
        type: "include",
        ids: new Set()
    });

    // Reset selection when popup opens
    useEffect(() => {
        if (open) {
            setRowSelectionModel({
                type: "include",
                ids: new Set()
            });
        }
    }, [open]);

    // DISPLAY ORDERS
    const handleDisplayOrders = async () => {
        setROWS([]);
        setLoadingOrders(true);

        try {
            const response = await fetch(`${apiUrl}manage_orders/get_all_orders.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    search,
                    status,
                    limit: 100,
                })
            });

            const result = await response.json();

            if (result.status === 'success') {

                // Create a quick payment lookup
                const paymentMap = new Map(
                    (result.payments || []).map((payment) => [
                        String(payment.OrderID),
                        Number(payment.PaidAmount) || 0
                    ])
                );

                const newRows = (result.data || []).map((row) => {

                    const price = Number(row.Price) || 0;
                    const itemQty = Number(row.ItemQty) || 0;
                    const sFee = Number(row.SFee) || 0;

                    // Get payment for this order

                    const paidAmount = paymentMap.get(String(row.OrderID)) || 0;
                    const totalAmount = (price * itemQty) + sFee;
                    const balance = totalAmount - paidAmount;

                    return {
                        ...row,
                        ItemAmount: totalAmount,
                        PaidAmount: paidAmount,
                        Balance: balance
                    };
                });

                setROWS(newRows);

            } else if (result.status === 'error') {
                Swal.fire({
                    title: 'Server Error!',
                    text: result.message,
                    icon: 'error'
                });
            }
        } catch (error) {
            Swal.fire({
                title: 'Server Error!',
                text: error,
                icon: 'error'
            });
            console.error('Error getting orders:', error);
        } finally {
            setLoadingOrders(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 400);

        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        handleDisplayOrders();
    }, [debouncedSearch, status]);

    const SELECTED_ROW = useMemo(() => {
        return ROWS.filter(row =>
            selectedIds?.includes(row.id)
        );
    }, [ROWS, selectedIds]);

    const COLUMNS = useMemo(() => [
        {
            field: 'CustomerName',
            headerName: 'Customer Name',
            flex: 1.5,
            minWidth: 200
        },
        {
            field: 'ItemName',
            headerName: 'Ordered Item',
            flex: 1,
            minWidth: 300,
        },
        {
            field: 'ItemQty',
            headerName: 'Qty',
            flex: 1,
            minWidth: 30,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <Typography
                        sx={{
                            fontSize: 13,
                            fontWeight: 600,
                        }}
                    >
                        {params.value}x
                    </Typography>
                </div>
            ),
        },
        {
            field: 'ItemAmount',
            headerName: 'ItemAmount',
            flex: 1,
            minWidth: 100,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <Typography
                        sx={{
                            fontSize: 13,
                            fontWeight: 600,
                        }}
                    >
                        {formatCurrency(params.value)}
                    </Typography>
                </div>
            ),
        },
        {
            field: 'Cargo',
            headerName: 'Cargo',
            flex: 1,
            minWidth: 100,
        },
        {
            field: 'PaidAmount',
            headerName: 'Paid Amount',
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <Typography
                        sx={{
                            fontSize: Number(params.value) > 0 ? 13 : 11,
                            fontWeight: 600,
                            color: Number(params.value) > 0
                                ? '#059669'
                                : '#94A3B8',
                        }}
                    >
                        {Number(params.value) > 0
                            ? formatCurrency(params.value)
                            : <em>No payment yet</em>}
                    </Typography>
                </div>
            ),
        },
        {
            field: 'Balance',
            headerName: 'Balance',
            flex: 1,
            minWidth: 80,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <Typography
                        sx={{
                            fontSize: Number(params.value) > 0 ? 13 : 11,
                            fontWeight: 600,
                            color: Number(params.value) > 0
                                ? '#c71f1f'
                                : '#94A3B8',
                        }}
                    >
                        {Number(params.value) > 0
                            ? formatCurrency(params.value)
                            : <em>Fully Paid</em>}
                    </Typography>
                </div>
            ),
        },
        {
            field: 'PayStatus',
            headerName: 'Payment Status',
            flex: 1,
            minWidth: 130,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    {chipPaymentStatus(params.value || "Pending")}
                </div>
            ),
        },
        {
            field: "OrderStat",
            headerName: "Order Status",
            flex: 1,
            minWidth: 130,
            cellClassName: "status-cell",
            renderCell: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        height: "100%",
                    }}
                >
                    {chipOrderStatus(params.value)}
                </Box>
            ),
        }

    ], []);

    const selectedCount =
        rowSelectionModel?.type === "include"
            ? rowSelectionModel.ids.size
            : ROWS.length - rowSelectionModel.ids.size;

    // ADD TO LIST FUNCTION
    const handleAddToList = async () => {

        const SELECTED_ORDER = SELECTED_ROW[0];

        const EXISTING_ORDER = listOrders.find(
            item =>
                String(item.OrderID) === String(SELECTED_ORDER.OrderID) &&
                String(item.CustomerID) === String(SELECTED_ORDER.CustomerID)
        );

        if (EXISTING_ORDER) {
            Swal.fire({
                text: "Already added to list.",
                icon: "info",
                timer: 2000,
                showConfirmButton: false
            });
            return;
        }

        if (!SELECTED_ORDER?.OrderID || !SELECTED_ORDER?.CustomerID || !listID) {
            Swal.fire({
                text: "Some ID number is missing!",
                icon: "warning",
            });
            return;
        }

        try {

            setLoadingAddOrder(true);

            const response = await fetch(
                `${apiUrl}manage_arrived/add_to_arrived_list.php`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                        custID: SELECTED_ORDER.CustomerID,
                        orderID: SELECTED_ORDER.OrderID,
                        listID: listID,
                    })
                }
            );

            const result = await response.json();

            if (result.status === "success") {

                const FORMATTED_ORDER = {
                    ListID: SELECTED_ORDER.ListID ?? "",
                    CustomerID: SELECTED_ORDER.CustomerID ?? "",
                    OrderID: SELECTED_ORDER.OrderID ?? "",

                    Remarks: SELECTED_ORDER.Remarks ?? "",
                    CustomerName: SELECTED_ORDER.CustomerName ?? "",
                    ItemName: SELECTED_ORDER.ItemName ?? "",
                    ItemCategory: SELECTED_ORDER.ItemCategory ?? "",
                    ItemQty: SELECTED_ORDER.ItemQty ?? "",
                    Price: SELECTED_ORDER.Price ?? "",
                    SFee: SELECTED_ORDER.SFee ?? "",
                    Cargo: SELECTED_ORDER.Cargo ?? "",
                    DateOrdered: SELECTED_ORDER.DateOrdered ?? "",
                    PaidAmount: SELECTED_ORDER.PaidAmount || 0,

                    OrderStat: result.orderStatus ?? "",
                    PayStatus: SELECTED_ORDER.PayStatus ?? ""
                };

                onUpdate(FORMATTED_ORDER);

                setROWS(prev =>
                    prev.map(item =>
                        item.OrderID === SELECTED_ORDER.OrderID &&
                            item.CustomerID === SELECTED_ORDER.CustomerID
                            ? {
                                ...item,
                                OrderStat: result.orderStatus,
                            }
                            : item
                    )
                );

                await Swal.fire({
                    title: "Added to List",
                    text: "Order has been added successfully.",
                    icon: "success",
                    timer: 1200,
                    showConfirmButton: false
                });

            } else {

                Swal.fire({
                    title: "Unable to Add Order",
                    text: result.message || "Failed to add order to list.",
                    icon: "error",
                });

            }

        } catch (error) {

            console.error(error);
            Swal.fire({
                title: "Server Error!",
                text: error.message || "Something went wrong.",
                icon: "error"
            });

        } finally {
            setLoadingAddOrder(false);
        }
    };

    return (
        <Backdrop
            aria-hidden="false"
            open={open}
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backdropFilter: "blur(1px)",
            }}
        >

            <Box
                sx={{
                    width: "95vw",
                    maxWidth: "1500px",
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    p: 2.5,
                    boxShadow: 6,

                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "4px",
                        background: "#2e3489"
                    }
                }}
            >

                <CustomModalHeader
                    title="Add Existing Order"
                    subtitle="Select an existing order to add to this list."
                    onClose={onClose}
                />

                {/* ACTION CONTROLS */}
                <Stack direction='row' gap={2}>
                    <CustomSearch
                        md="36ch"
                        placeholder='Search by Customer Name'
                        value={search}
                        onChange={(val) => setSearch(val)}
                    />

                    <CustomButton
                        label="Refresh"
                        startIcon={loadingOrders ?
                            <CircularProgress size="16px" sx={{
                                color: "#9ca3af"
                            }} /> :
                            <RefreshIcon fontSize="small" />
                        }
                        disabled={loadingOrders || loadingAddOrder}
                        onClick={() => handleDisplayOrders()}
                    />

                    <CustomSelect
                        label="Filter by Status"
                        disabled={loadingOrders}
                        minWidth={160}
                        value={status}
                        onChange={setStatus}
                        options={[
                            { label: <em>All</em>, value: "" },
                            { label: "Pending", value: "Pending" },
                            { label: "Confirmed", value: "Confirmed" },
                            { label: "In Transit", value: "In_Transit" },
                            { label: "Arrived", value: "Arrived" },
                            { label: "Shipped", value: "Shipped" },
                            { label: "Cancelled", value: "Cancelled" },
                            { label: "Forfeited", value: "Forfeited" },
                            { label: "Disposed", value: "Disposed" },
                        ]}
                    />
                </Stack>

                {/* DATAGRID DISPLAY DATA */}
                <DataGrid
                    autoHeight
                    checkboxSelection
                    rows={ROWS}
                    columns={COLUMNS}
                    loading={loadingOrders}

                    localeText={{
                        noRowsLabel: loadingOrders
                            ? ""
                            : (<SearchNotFound />)
                    }}

                    disableMultipleRowSelection
                    rowSelectionModel={rowSelectionModel}
                    onRowSelectionModelChange={(newSelection) => {
                        const ids = Array.from(newSelection.ids);
                        setSelectedIds(ids);
                        setRowSelectionModel(newSelection);
                    }}

                    getRowClassName={(params) =>
                        params.indexRelativeToCurrentPage % 2 === 0
                            ? "even"
                            : "odd"
                    }

                    initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                    }}

                    pageSizeOptions={[10]}
                    disableColumnResize
                    disableColumnMenu
                    disableColumnSorting
                    density="compact"
                    sx={{
                        mt: 2,

                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#1C2536',
                            borderBottom: 'none',
                        },

                        '& .MuiDataGrid-columnHeader': {
                            backgroundColor: '#1C2536',
                        },

                        '& .MuiDataGrid-columnHeaderTitle': {
                            color: '#94A3B8',
                            fontWeight: 700,
                            fontSize: 11,
                            letterSpacing: '0.6px',
                            textTransform: 'uppercase',
                        },

                        // REMOVE CLICK BORDER
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                            outline: 'none',
                        },

                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                            outline: 'none',
                        },

                        // NORMAL HOVER
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: '#dcfbff',
                            cursor: 'pointer',
                        },

                        // SELECTED ROW
                        '& .MuiDataGrid-row.Mui-selected': {
                            backgroundColor: '#cfe8ff',
                        },

                        // SELECTED + HOVER
                        '& .MuiDataGrid-row.Mui-selected:hover': {
                            backgroundColor: '#b6dcff',
                        },
                    }}

                    slotProps={{
                        loadingOverlay: {
                            variant: 'skeleton',
                        },

                        filterPanel: {
                            filterFormProps: {
                                logicOperatorInputProps: {
                                    variant: 'outlined',
                                    size: 'small',
                                },

                                columnInputProps: {
                                    variant: 'outlined',
                                    size: 'small',
                                    sx: {
                                        mt: 'auto'
                                    },
                                },

                                operatorInputProps: {
                                    variant: 'outlined',
                                    size: 'small',
                                    sx: {
                                        mt: 'auto'
                                    },
                                },

                                valueInputProps: {
                                    InputComponentProps: {
                                        variant: 'outlined',
                                        size: 'small',
                                    },
                                },
                            },
                        },
                    }}
                />

                {/* ACTIONS */}
                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    spacing={2}
                    mt={2}
                >

                    <CustomButton
                        label="Cancel"
                        variant="outlined"
                        disabled={loadingOrders || loadingAddOrder}
                        hoverColor="#d14249"
                        onClick={onClose}
                    />

                    <CustomButton
                        width={160}
                        label={loadingAddOrder ? "Adding..." : "Add to List"}
                        disabled={selectedCount === 0 || loadingOrders || loadingAddOrder}
                        onClick={handleAddToList}
                    />

                </Stack>

            </Box>

        </Backdrop>
    );
};


export default AddOrder;